import React from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import {
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { AnomalyCase } from './types';

export default function PropertyOutageDetails({ anomalyCase: c }: { anomalyCase: AnomalyCase }) {
  const days = c.affectedProducts
    .flatMap((product) => product.dailyState ?? [])
    .sort((left, right) => left.stay_date.localeCompare(right.stay_date));

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        The stored ARI evidence indicates every eligible product is closed or has zero inventory on {c.nightsAffected}
        nights. Verify current availability using an eligible stay length, then confirm whether this is a sell-out, a
        planned closure or an ARI problem. Missing pricing alone does not establish an outage.
      </Alert>
      <Typography variant="subtitle2" gutterBottom>
        Whole-property ARI evidence
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Stay night</TableCell>
              <TableCell align="right">Live products</TableCell>
              <TableCell align="right">Sellable</TableCell>
              <TableCell align="right">Explicitly blocked</TableCell>
              <TableCell align="right">Closed</TableCell>
              <TableCell align="right">Stocked, no price</TableCell>
              <TableCell align="right">No stock</TableCell>
              <TableCell align="right">No product ARI</TableCell>
              <TableCell align="right">Unresolved</TableCell>
              <TableCell>Verify</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => (
              <TableRow key={day.stay_date} selected={day.unavailable_flag}>
                <TableCell>{day.stay_date}</TableCell>
                <TableCell align="right">{day.live_products}</TableCell>
                <TableCell align="right">{day.sellable_products}</TableCell>
                <TableCell align="right">{day.blocked_products}</TableCell>
                <TableCell align="right">{day.closed_products}</TableCell>
                <TableCell align="right">{day.unpriced_stocked_products}</TableCell>
                <TableCell align="right">{day.zero_inventory_products}</TableCell>
                <TableCell align="right">{day.missing_products}</TableCell>
                <TableCell align="right">{day.unknown_products}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!c.accommodationPropertyId}
                    href={
                      c.accommodationPropertyId
                        ? `/accommodation/properties/${c.accommodationPropertyId}/rates`
                        : undefined
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<LaunchIcon />}
                  >
                    Review availability
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!days.length && (
              <TableRow>
                <TableCell colSpan={10}>Daily evidence has not loaded yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
